import { cn } from '@/lib/utils';
import { type ComponentProps, forwardRef } from 'react';

/**
 * Defaults anti-autofill: o navegador ignora `autoComplete="off"` no <form>,
 * só respeita quando aplicado em cada <input>. Por isso defaultamos aqui —
 * forms auth (login/signup/reset/forgot) sobrescrevem com valor semântico
 * (`email`, `current-password`, `new-password`, `name`) que ativa autofill
 * legítimo. Tudo o mais (label de connection, WABA ID, nome de workspace,
 * URL de webhook, etc.) fica protegido por default.
 *
 * Por que importa: form de Connection com label autopreenchido com email do
 * user, ou WABA ID com nome salvo, é confuso e propenso a erro. Pior: ataque
 * conhecido onde browser preenche campos hidden de email/senha em forms que
 * parecem inocentes.
 */
export const Input = forwardRef<HTMLInputElement, ComponentProps<'input'>>(
  ({ className, type, autoComplete = 'off', ...props }, ref) => (
    <input
      type={type}
      autoComplete={autoComplete}
      className={cn(
        'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors',
        'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
        'placeholder:text-muted-foreground',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      ref={ref}
      {...props}
    />
  ),
);
Input.displayName = 'Input';
